import React, { useEffect, useState } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { ICurriculumForHome } from '../../../store/CurriculumSlice';
import {
  getUrlConvertedString,
  getSubjectColorNew,
  getSubjectIconName,
  checkSubjectCodeHide,
} from '../../../helper';
import SearchBar from '../components/SearchBar';

interface CurriculumModalProps {
  open: boolean;
  onClose: () => void;
  curriculumsForHomePage: Array<ICurriculumForHome>;
  isDiagnosticFlow: boolean;
}

const CurriculumModal: React.FC<CurriculumModalProps> = ({
  open,
  onClose,
  curriculumsForHomePage,
  isDiagnosticFlow,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  const [
    currentCurriculum,
    setCurrentCurriculum,
  ] = useState<ICurriculumForHome | null>(curriculumsForHomePage[0]);
  const [subjectsVisible, setSubjectsVisible] = useState(true);

  useEffect(() => {
    if (open && curriculumsForHomePage.length > 0) {
      setCurrentCurriculum(curriculumsForHomePage[0]);
      setSubjectsVisible(true);
    }
  }, [open, curriculumsForHomePage]);

  const handleCurriculumClick = (curriculum: ICurriculumForHome) => {
    if (currentCurriculum?.name === curriculum.name) {
      setSubjectsVisible((prev) => !prev);
    } else {
      setCurrentCurriculum(curriculum);
      setSubjectsVisible(true);
    }
  };

  const isSubjectCodeHide = checkSubjectCodeHide(currentCurriculum?.name || '');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          padding: '10px',
          borderRadius: '30px',
          width: '800px',
          maxHeight: '600px',
        },
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogContent sx={{ borderRadius: 3, padding: 3 }}>
        {/* Search Bar */}
        <Box marginBottom={2}>
          <SearchBar />
        </Box>
        {/* Divider */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: belowMd ? 'column' : 'row',
            gap: 2,
          }}
        >
          {/* Curriculums */}
          <Box
            sx={{
              width: belowMd ? '100%' : '33%',
              maxHeight: '400px',
              overflowY: 'auto',
              pr: 2,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '8px',
              },
            }}
          >
            {curriculumsForHomePage.map((c) => {
              const isSelected = currentCurriculum?.name === c.name;

              return (
                <Box key={c.name}>
                  {/* Curriculum Button */}
                  <Box
                    onClick={() => handleCurriculumClick(c)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '8px',
                      px: 2,
                      py: 2,
                      fontWeight: 700,
                      mb: '10px',
                      bgcolor: isSelected ? '#E2E8F0' : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected ? '#E2E8F0' : 'action.hover',
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      {c.name}
                      {belowMd && (
                        <ExpandMoreIcon
                          sx={{
                            transform:
                              isSelected && subjectsVisible
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Subjects shown only if selected */}
                  {belowMd && isSelected && subjectsVisible && (
                    <Collapse in={subjectsVisible}>
                      <Box display="flex" flexDirection="column" gap={1} pl={2}>
                        {c.subjects.map((subj, index) => {
                          const curriculum = getUrlConvertedString(c.name);
                          const subject = getUrlConvertedString(subj.name);
                          const subjectLevel = getUrlConvertedString(
                            subj.subjectLevel
                          );
                          const subjectCode = getUrlConvertedString(
                            subj.subjectCode
                          );

                          return (
                            <Card
                              key={index}
                              elevation={0}
                              sx={{
                                backgroundColor: `${getSubjectColorNew(
                                  subj.name
                                )}1A`,
                                borderRadius: '7px',
                                '&:hover': {
                                  backgroundColor: `${getSubjectColorNew(
                                    subj.name
                                  )}4A`,
                                },
                              }}
                            >
                              <CardActionArea
                                onClick={() => {
                                  const baseUrl = `${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}`;
                                  const fullUrl = isDiagnosticFlow
                                    ? `${baseUrl}?tab=1`
                                    : baseUrl;
                                  navigate(fullUrl);
                                }}
                              >
                                <CardContent>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={2}
                                    >
                                      <img
                                        src={`${
                                          process.env.PUBLIC_URL
                                        }/images/landingPage/newLandingPage/icons/icon-${getSubjectIconName(
                                          subj.name
                                        )}.png`}
                                        alt="subject-img"
                                        style={{ height: '18px' }}
                                      />
                                      <Box>
                                        <Typography
                                          variant="subtitle1"
                                          fontSize="11px"
                                          fontWeight={700}
                                          className="ellipsis-text"
                                        >
                                          {subj.name}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          fontSize="10px"
                                        >
                                          {isSubjectCodeHide
                                            ? subj.grade
                                            : subj.subjectCode}{' '}
                                          ({subj.subjectLevel})
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Stack>
                                </CardContent>
                              </CardActionArea>
                            </Card>
                          );
                        })}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Subjects */}
          {!belowMd && subjectsVisible && (
            <Box
              sx={{
                width: belowMd ? '100%' : '67%',
                maxHeight: '400px',
                overflowY: 'auto',
                pr: 2,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#c1c1c1',
                  borderRadius: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '8px',
                },
              }}
            >
              <Collapse in={subjectsVisible || !belowMd}>
                <Box
                  display="grid"
                  gridTemplateColumns={belowMd ? '1fr' : 'repeat(2, 1fr)'}
                  gap={2}
                >
                  {currentCurriculum?.subjects.map((subj, index) => {
                    const curriculum = getUrlConvertedString(
                      currentCurriculum.name
                    );
                    const subject = getUrlConvertedString(subj.name);
                    const subjectLevel = getUrlConvertedString(
                      subj.subjectLevel
                    );
                    const subjectCode = getUrlConvertedString(subj.subjectCode);

                    return (
                      <Card
                        key={index}
                        elevation={0}
                        sx={{
                          backgroundColor: `${getSubjectColorNew(subj.name)}1A`,
                          borderRadius: '7px',
                          '&:hover': {
                            backgroundColor: `${getSubjectColorNew(
                              subj.name
                            )}4A`,
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() => {
                            const baseUrl = `${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}`;
                            const fullUrl = isDiagnosticFlow
                              ? `${baseUrl}?assessment-tab=da`
                              : baseUrl;
                            navigate(fullUrl);
                          }}
                        >
                          <CardContent>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Box display="flex" alignItems="center" gap={2}>
                                <img
                                  src={`${
                                    process.env.PUBLIC_URL
                                  }/images/landingPage/newLandingPage/icons/icon-${getSubjectIconName(
                                    subj.name
                                  )}.png`}
                                  alt="subject-img"
                                  style={{ height: '18px' }}
                                />
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    fontSize="13px"
                                    fontWeight={700}
                                    className="ellipsis-text"
                                  >
                                    {subj.name}
                                  </Typography>
                                  <Typography variant="body2" fontSize="12px">
                                    {isSubjectCodeHide
                                      ? subj.grade
                                      : subj.subjectCode}{' '}
                                    ({subj.subjectLevel})
                                  </Typography>
                                </Box>
                              </Box>
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CurriculumModal;
